import React from 'react';
import styled from 'styled-components';
import { config } from '../../config';

const PolicyContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 20px 40px;
  
  @media (max-width: 768px) {
    padding: 60px 20px 40px;
  }
`;

const Title = styled.h1`
  font-size: 36px;
  color: #333;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const LastUpdated = styled.p`
  color: #666;
  margin-bottom: 32px;
  font-size: 14px;
`;

const Section = styled.section`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  color: #333;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const Paragraph = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #555;
  margin-bottom: 16px;
`;

const List = styled.ul`
  margin: 16px 0 16px 24px;
  
  li {
    font-size: 16px;
    line-height: 1.8;
    color: #555;
    margin-bottom: 8px;
  }
`;

const HighlightBox = styled.div`
  background: #f8f9fa;
  border-left: 4px solid #667eea;
  padding: 16px 20px;
  margin: 20px 0;
  border-radius: 4px;
`;

const RefundPolicy = () => {
  return (
    <PolicyContainer>
      <Title>Refund and Cancellation Policy</Title>
      <LastUpdated>Last Updated: December 28, 2024</LastUpdated>

      <Section>
        <Paragraph>
          At {config.company.name}, we strive to provide high-quality services. This Refund and 
          Cancellation Policy outlines the terms under which refunds may be requested and processed.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>1. Refund Eligibility</SectionTitle>
        <Paragraph>Refunds may be requested under the following conditions:</Paragraph>
        <List>
          <li><strong>7-Day Money-Back Guarantee:</strong> Full refund available within 7 days of purchase if you haven't accessed more than 20% of the course content</li>
          <li><strong>Technical Issues:</strong> If we are unable to provide the service due to technical problems on our end</li>
          <li><strong>Duplicate Payment:</strong> If you have been charged multiple times for the same transaction</li>
          <li><strong>Service Not Delivered:</strong> If the promised service was not delivered within the specified timeframe</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>2. Non-Refundable Situations</SectionTitle>
        <Paragraph>Refunds will NOT be provided in the following cases:</Paragraph>
        <List>
          <li>After 7 days from the date of purchase</li>
          <li>If more than 20% of the course content has been accessed</li>
          <li>Change of mind or personal circumstances</li>
          <li>Failure to attend live sessions without prior notice</li>
          <li>Violation of our Terms and Conditions</li>
          <li>Downloaded course materials or certificates</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>3. Cancellation Policy</SectionTitle>
        <Paragraph>Cancellation terms:</Paragraph>
        <List>
          <li><strong>Before Course Start:</strong> Full refund minus payment gateway charges (2-3%)</li>
          <li><strong>Within 7 Days of Purchase:</strong> Full refund if less than 20% content accessed</li>
          <li><strong>After 7 Days:</strong> No refund available</li>
          <li><strong>Live Sessions:</strong> Must cancel 24 hours before scheduled session</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>4. Refund Process</SectionTitle>
        <Paragraph>To request a refund:</Paragraph>
        <List>
          <li>Send an email to {config.company.email} with your order details</li>
          <li>Include your transaction ID and reason for refund</li>
          <li>Our team will review your request within 2-3 business days</li>
          <li>If approved, refund will be processed within 7-10 business days</li>
          <li>Refund will be credited to the original payment method</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>5. Payment Gateway Charges</SectionTitle>
        <Paragraph>
          Payment gateway charges (typically 2-3% of transaction amount) are non-refundable. 
          These charges are deducted by PhonePe and other payment processors and cannot be 
          recovered by us.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>6. Partial Refunds</SectionTitle>
        <Paragraph>
          Partial refunds may be considered on a case-by-case basis for long-term programs. 
          The refund amount will be calculated based on the unused portion of the service, 
          minus administrative fees.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>7. Course Transfer</SectionTitle>
        <Paragraph>
          Instead of a refund, you may request to transfer your enrollment to another course 
          of equal or lesser value, subject to availability and approval.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>8. Failed Transactions</SectionTitle>
        <Paragraph>
          If your payment was deducted but the transaction failed:
        </Paragraph>
        <List>
          <li>The amount will be automatically refunded by your bank within 5-7 business days</li>
          <li>If not received, contact us with transaction details</li>
          <li>We will coordinate with PhonePe to resolve the issue</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>9. Refund Timeline</SectionTitle>
        <HighlightBox>
          <Paragraph style={{ marginBottom: 0 }}>
            <strong>Processing Time:</strong> 2-3 business days for review<br />
            <strong>Refund Credit:</strong> 7-10 business days after approval<br />
            <strong>Bank Processing:</strong> Additional 3-5 days depending on your bank
          </Paragraph>
        </HighlightBox>
      </Section>

      <Section>
        <SectionTitle>10. Dispute Resolution</SectionTitle>
        <Paragraph>
          If you are not satisfied with our refund decision, you may escalate the matter by:
        </Paragraph>
        <List>
          <li>Sending a detailed explanation to {config.company.email}</li>
          <li>Our senior management will review within 5 business days</li>
          <li>Final decision will be communicated via email</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>11. Contact for Refunds</SectionTitle>
        <Paragraph>
          For refund requests or questions about this policy, contact us:
        </Paragraph>
        <List>
          <li><strong>Email:</strong> {config.company.email}</li>
          <li><strong>Phone:</strong> {config.company.phone}</li>
          <li><strong>Response Time:</strong> Within 24-48 hours</li>
        </List>
      </Section>
    </PolicyContainer>
  );
};

export default RefundPolicy;
